import { EventCardEvent } from "features/CenterContent/RoleContent/EventHandling/components/eventCard/types";
import EventChangesDialog from "features/CenterContent/RoleContent/EventHandling/components/EventChangesDialog/EventChangesDialog";
import { PlanMeasureKey } from "features/CenterContent/RoleContent/EventHandling/components/EventChangesDialog/types";
import { EventTimeInfo } from "features/CenterContent/RoleContent/EventHandling/components/InfraEventCard/EventTimeInfo";
import { MuteHistoryBox } from "features/CenterContent/RoleContent/EventHandling/components/InfraEventCard/MuteHistoryBox";
import {
  getSemantics,
  useColorScheme,
} from "features/CenterContent/RoleContent/EventHandling/components/InfraEventCard/useColorScheme";
import { useEventCardState } from "features/CenterContent/RoleContent/EventHandling/components/InfraEventCard/useEventCardState";
import MuteEventDialog from "features/CenterContent/RoleContent/EventHandling/components/MuteEventDialog";
import { EventCardContainer } from "features/CenterContent/RoleContent/EventHandling/components/shared/EventCardContainer";
import { useMuteActions } from "features/CenterContent/RoleContent/EventHandling/components/shared/useMuteActions";
import { usePreviousEventVersion } from "features/CenterContent/RoleContent/EventHandling/components/shared/usePreviousEventVersion";
import { FC, ReactNode } from "react";

export type EventCardShellProps = {
  event: EventCardEvent;
  eventTitle: string;
  columnEventState?: PlanMeasureKey;
  children: (context: {
    colorScheme: ReturnType<typeof useColorScheme>;
    semantics: ReturnType<typeof getSemantics>;
    changed: boolean;
    notifying: boolean;
    canToggleNotify: boolean;
    hover: boolean;
    columnEventState: PlanMeasureKey;
    onToggleNotify: () => void;
    closed: boolean;
  }) => ReactNode;
};

export const EventCardShell: FC<EventCardShellProps> = ({
  event,
  eventTitle,
  columnEventState,
  children,
}) => {
  const { data: previousEvent } = usePreviousEventVersion(event);

  const {
    changed,
    notifying,
    setNotifying,
    muteDialogOpen,
    setMuteDialogOpen,
    changeDialogOpen,
    setChangeDialogOpen,
    handleDialogConfirmed,
    handleMuted,
    muted,
    muteHistory,
    handleUnmuted,
  } = useEventCardState(event, previousEvent, columnEventState);

  const closed = event.state === "CLOSED";
  const effectiveChanged = changed && !closed;
  const effectiveColumnEventState = columnEventState ?? "noCoverage";
  const canToggleNotify = effectiveColumnEventState === "noCoverage" && !closed;

  const { muteDialogMode, handleToggleNotify } = useMuteActions({
    notifying,
    muted,
    canToggleNotify,
    setNotifying,
    setMuteDialogOpen,
  });

  const colorScheme = useColorScheme(effectiveChanged, notifying, closed);
  // No hover on train events as they are not clickable
  const trainEvent = "trainIdentifier" in event;

  return (
    <>
      <EventCardContainer
        uuid={event.uuid}
        colorScheme={colorScheme}
        disabled={trainEvent}
      >
        {(hover) => (
          <>
            {children({
              colorScheme,
              semantics: getSemantics(effectiveChanged, notifying),
              changed: effectiveChanged,
              notifying,
              canToggleNotify,
              hover,
              columnEventState: effectiveColumnEventState,
              onToggleNotify: handleToggleNotify,
              closed,
            })}

            {!closed && (
              <MuteHistoryBox
                muteHistory={muteHistory}
                colorScheme={colorScheme}
                // No hover on train events as they are not clickable
                hover={hover && !trainEvent}
              />
            )}

            <EventTimeInfo
              event={event}
              colorScheme={colorScheme}
              changed={effectiveChanged}
              onShowChanges={() => setChangeDialogOpen(true)}
            />
          </>
        )}
      </EventCardContainer>

      <MuteEventDialog
        open={muteDialogOpen}
        setopen={setMuteDialogOpen}
        eventTitle={eventTitle}
        mode={muteDialogMode}
        onMuted={(reason) => handleMuted(reason)}
        onUnmuted={(reason) => handleUnmuted(reason)}
      />

      <EventChangesDialog
        open={changeDialogOpen}
        setopen={setChangeDialogOpen}
        event={event}
        previousEvent={previousEvent}
        onConfirmed={handleDialogConfirmed}
      />
    </>
  );
};
